import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryService } from '../services/queryService';
import { useNotification } from '../context/NotificationContext';
import { ExecuteQueryRequest, ValidationResponse } from '../types';
import { terminalOptions } from '../utils/terminalConfig';
import { extractParametersFromSql, QueryParameterValue } from '../utils/queryUtils';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import { generateNextVersion, generateNextVersionFromHistory } from '../utils/versionUtils';
import { resolveAiModelOptions } from '../pages/CreateQueryPage';

export const useQueryDetailController = (queryId: string) => {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const queryClient = useQueryClient();
    const { hasRole, user } = useAuth();
    
    // Permissions
    const canCreateVersions = hasRole(roleConfig.createVersions);
    const canActivateQueries = hasRole(roleConfig.activateQueries);

    const [tabValue, setTabValue] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTerminal, setSelectedTerminal] = useState('');
    const [selectedDatabase, setSelectedDatabase] = useState<'ORACLE' | 'POSTGRESQL' | 'EXTERNAL'>('ORACLE');
    const [selectedConnection, setSelectedConnection] = useState('');
    const [selectedVersion, setSelectedVersion] = useState<string>('current');
    const [executionParameters, setExecutionParameters] = useState<QueryParameterValue[]>([]);
    const [executionError, setExecutionError] = useState<string | null>(null);
    const [validationResult, setValidationResult] = useState<ValidationResponse | null>(null);
    const [lastExecutionResult, setLastExecutionResult] = useState<any>(null);
    const [isExplainingPlan, setIsExplainingPlan] = useState(false);
    const [aiExplanation, setAiExplanation] = useState<string | null>(null);
    const [isGeneratingAiExplanation, setIsGeneratingAiExplanation] = useState(false);

    // AI Model selection state
    const aiModelOptions = useMemo(() => resolveAiModelOptions(), []);
    const [aiModelName, setAiModelName] = useState(() => {
        const options = resolveAiModelOptions();
        return options[0]?.value ?? 'gpt-4o-mini';
    });

    // Version Creation Dialog State
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);
    const [loadingVersionNumber, setLoadingVersionNumber] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [versionForm, setVersionForm] = useState({
        versionNumber: '',
        sqlContent: '',
        changeDescription: '',
        createdBy: user?.name || user?.username || 'Unknown User',
        queryId: queryId,
        makeCurrent: true
    });

    // Refs for state access in handlers
    // Mutable state object pattern due to potential closure issues
    const terminalRef = useRef({ current: selectedTerminal }).current;
    const dbRef = useRef({ current: selectedDatabase }).current;
    const connectionRef = useRef({ current: selectedConnection }).current;
    const paramsRef = useRef({ current: executionParameters }).current;
    const qRef = useRef<{ current: any | undefined }>({ current: undefined }).current;
    const versionFormRef = useRef(versionForm);

    useEffect(() => { terminalRef.current = selectedTerminal; }, [selectedTerminal]);
    useEffect(() => { dbRef.current = selectedDatabase; }, [selectedDatabase]);
    useEffect(() => { connectionRef.current = selectedConnection; }, [selectedConnection]);
    useEffect(() => { paramsRef.current = executionParameters; }, [executionParameters]);
    useEffect(() => { versionFormRef.current = versionForm; }, [versionForm]);

    // Fetch query details
    const { data: query, isLoading, error } = useQuery({
        queryKey: ['query', queryId],
        queryFn: () => queryService.getQuery(queryId!),
        enabled: !!queryId,
    });

    // Update query ref when query data changes (moved here to avoid used-before-declaration error)
    useEffect(() => { qRef.current = query; }, [query]);

    // Align execute controls with query metadata, including external connection mode.
    useEffect(() => {
        if (!query) return;
        const dbType = String(query.databaseType || 'ORACLE').toUpperCase();
        if (dbType === 'EXTERNAL') {
            setSelectedDatabase('EXTERNAL');
            setSelectedConnection(query.connectionId || '');
            return;
        }
        if (dbType === 'POSTGRESQL' || dbType === 'ORACLE') {
            setSelectedDatabase(dbType as 'POSTGRESQL' | 'ORACLE');
            setSelectedConnection('');
        }
    }, [query?.queryId, query?.databaseType, query?.connectionId]);


    // Load version history
    const { data: versionHistory } = useQuery({
        queryKey: ['query-versions', queryId],
        queryFn: () => queryService.getVersionHistory(queryId!),
        enabled: !!queryId,
    });

    // Fetch schedules
    const { data: schedules } = useQuery({
        queryKey: ['schedules'],
        queryFn: () => queryService.listSchedules(),
    });

    // Extract parameters when query loads
    useEffect(() => {
        if (query?.sqlContent) {
            const extracted = extractParametersFromSql(query.sqlContent);

            setExecutionParameters(prev => {
                return extracted.map(newParam => {
                    const existingState = prev.find(p => p.name === newParam.name);
                    const definedParam = query.parameters?.find((p: any) => p.name === newParam.name);

                    let type = newParam.type;
                    let value = newParam.value;

                    if (existingState) {
                        type = existingState.type;
                        value = existingState.value;
                    } else if (definedParam) {
                        if (definedParam.type) type = definedParam.type as any;
                        if (definedParam.defaultValue) value = definedParam.defaultValue;
                    }

                    return { ...newParam, type, value };
                });
            });
        }
    }, [query?.sqlContent, query?.parameters]);

    // Execute query mutation
    const executeMutation = useMutation({
        mutationFn: (data: ExecuteQueryRequest) => {
            if (selectedVersion === 'current') {
                return queryService.executeQuery(queryId!, data);
            } else {
                return queryService.executeQueryVersion(queryId!, selectedVersion, data);
            }
        },
        onSuccess: (response) => {
            setLastExecutionResult(response);
            if (response.status === 'ERROR') {
                let errorMessage = response.errorMessage || 'Unknown execution error';
                if (errorMessage.includes('No DataSource configured')) {
                    const availableTenantsMatch = errorMessage.match(/Available tenants: \[(.*?)\]/);
                    if (availableTenantsMatch) {
                        const availableTenants = availableTenantsMatch[1].replace(/"/g, '').split(', ');
                        errorMessage = `Terminal "${selectedTerminal}" is not configured. Available terminals: ${availableTenants.join(', ')}.`;
                    }
                }
                setExecutionError(errorMessage);
            } else if (response.status === 'SUCCESS') {
                showNotification(`Query executed successfully! ${response.resultCount || 0} rows returned.`, 'success');
            } else {
                showNotification(`Query execution completed with status: ${response.status}`, 'warning');
            }
        },
        onError: (error: any) => {
            let errorMessage = error.response?.data?.errorMessage || error.response?.data?.message || error.message;
            setExecutionError(errorMessage);
        },
    });

    // Delete query mutation
    const deleteMutation = useMutation({
        mutationFn: () => queryService.deleteQuery(queryId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['queries'] });
            showNotification('Query deleted successfully', 'success');
            navigate('/queries');
        },
        onError: (error: any) => {
            showNotification(`Failed to delete query: ${error.response?.data?.message || error.message}`, 'error');
        },
    });

    // Toggle activation mutation
    const toggleActivationMutation = useMutation({
        mutationFn: (active: boolean) => queryService.setQueryActivation(queryId!, active, 'current-user'),
        onSuccess: (response) => {
            showNotification(`Query ${response.isActive ? 'activated' : 'deactivated'} successfully`, 'success');
            queryClient.invalidateQueries({ queryKey: ['query', queryId] });
        },
        onError: (error: any) => {
            showNotification(`Failed to update query status: ${error.response?.data?.message || error.message}`, 'error');
        },
    });

    // Set current version mutation
    const setCurrentVersionMutation = useMutation({
        mutationFn: (versionNumber: string) => queryService.setCurrentVersion(queryId!, versionNumber),
        onSuccess: () => {
            showNotification('Version set as current successfully', 'success');
            queryClient.invalidateQueries({ queryKey: ['query', queryId] });
            queryClient.invalidateQueries({ queryKey: ['query-versions', queryId] });
        },
        onError: (error: any) => {
            showNotification(`Failed to set current version: ${error.response?.data?.message || error.message}`, 'error');
        },
    });

    // Create version mutation
    const createVersionMutation = useMutation({
        mutationFn: async (versionData: any) => {
            const newVersion = await queryService.createVersion(queryId, versionData);
            if (versionFormRef.current?.makeCurrent) {
                await queryService.setCurrentVersion(queryId, versionData.versionNumber);
            }
            return newVersion;
        },
        onSuccess: (newVersion, variables) => {
            queryClient.invalidateQueries({ queryKey: ['query', queryId] });
            queryClient.invalidateQueries({ queryKey: ['query-versions', queryId] });
            queryClient.invalidateQueries({ queryKey: ['queries'] });
            
            setVersionDialogOpen(false);
            setDialogError(null);
            
            const actionLabel = variables.makeCurrent ? 'created and activated!' : 'created!';
            showNotification(`Version ${variables.versionNumber} ${actionLabel}`, 'success');
        },
        onError: (error: any) => {
            setDialogError(error.response?.data?.message || error.message || 'Failed to create version');
        }
    });

    const handleExecute = () => {
        if (executeMutation.isPending) return;
        const currentTerminal = terminalRef.current;
        const currentDatabase = dbRef.current;
        const currentConnection = connectionRef.current;
        const currentParams = paramsRef.current;

        console.log('DEBUG: handleExecute called');
        console.log('DEBUG: State - selectedTerminal:', currentTerminal);
        console.log('DEBUG: State - selectedDatabase:', currentDatabase);

        const isExternal = currentDatabase === 'EXTERNAL';
        if (isExternal && !currentConnection) {
            showNotification('Please select a connection before executing', 'warning');
            return;
        }
        if (!isExternal && !currentTerminal) {
            showNotification('Please select a terminal before executing', 'warning');
            return;
        }
        const isPostgreSQL = currentDatabase === 'POSTGRESQL';
        console.log('DEBUG: isPostgreSQL:', isPostgreSQL);

        const terminalOption = terminalOptions.find(opt => opt.id === currentTerminal);
        console.log('DEBUG: terminalOption:', terminalOption);

        const terminalId = isExternal
            ? (currentTerminal || 'EXTERNAL')
            : (isPostgreSQL ? (terminalOption?.rawId || currentTerminal) : (terminalOption?.id || currentTerminal));
        console.log('DEBUG: computed terminalId:', terminalId);

        const parameters: Record<string, any> = {};
        currentParams.forEach((p) => {
            if (p.value !== undefined && p.value !== '') {
                if (p.type === 'NUMBER') parameters[p.name] = Number(p.value);
                else if (p.type === 'BOOLEAN') parameters[p.name] = p.value.toLowerCase() === 'true';
                else parameters[p.name] = p.value;
            }
        });

        const executeData: ExecuteQueryRequest = {
            terminalId,
            parameters,
            returnResults: true,
            maxResults: 50,
            ...(isPostgreSQL && { snapshot: true }),
            ...(isExternal && currentConnection
                ? { databaseType: 'EXTERNAL', connectionId: currentConnection }
                : {}),
        };

        console.log('DEBUG: executeData payload:', executeData);
        setExecutionError(null);
        executeMutation.mutate(executeData);
    };

    const handleParameterChange = (index: number, value: string) => {
        const newParams = [...executionParameters];
        newParams[index].value = value;
        setExecutionParameters(newParams);
    };

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showNotification('Copied to clipboard', 'success');
        });
    };

    const handleCreateVersion = async () => {
        if (!query) return;

        setDialogError(null);
        setLoadingVersionNumber(true);
        setVersionDialogOpen(true);

        const createdBy = user?.name || user?.username || 'Unknown User';
        
        // Initial estimation
        const initialNextVersion = generateNextVersion(query.currentVersion);
        setVersionForm(prev => ({
            ...prev,
            versionNumber: initialNextVersion,
            sqlContent: query.sqlContent || '',
            createdBy,
            makeCurrent: true
        }));

        try {
            const history = await queryService.getVersionHistory(queryId, 100);
            const nextVersion = generateNextVersionFromHistory(history, String(query.currentVersion));
            
            setVersionForm(prev => ({
                ...prev,
                versionNumber: nextVersion
            }));
        } catch (error) {
            console.error('Failed to generate next version number:', error);
        } finally {
            setLoadingVersionNumber(false);
        }
    };

    const handleVersionSubmit = () => {
        if (createVersionMutation.isPending) return;
        const currentForm = versionFormRef.current;
        
        if (!currentForm.versionNumber || !currentForm.sqlContent) {
            setDialogError('Version number and SQL content are required');
            return;
        }

        createVersionMutation.mutate({
            versionNumber: currentForm.versionNumber,
            sqlContent: currentForm.sqlContent,
            changeNotes: currentForm.changeDescription,
            createdBy: currentForm.createdBy,
            makeCurrent: currentForm.makeCurrent
        });
    };

    const handleExplainPlan = async () => {
        if (!queryId || isExplainingPlan) return;
        
        setIsExplainingPlan(true);
        setValidationResult(null); // Clear previous to show new plan
        setAiExplanation(null);
        
        try {
            const params: Record<string, any> = {};
            executionParameters.forEach(p => {
                if (p.value !== undefined && p.value !== '') {
                    params[p.name] = p.value;
                }
            });

            const isExternal = selectedDatabase === 'EXTERNAL';
            if (isExternal && !selectedConnection) {
                showNotification('Please select a connection before generating explain plan', 'warning');
                return;
            }

            const result = await queryService.validateQuery({
                sqlContent: query?.sqlContent || '',
                terminalId: isExternal ? (selectedTerminal || 'EXTERNAL') : selectedTerminal,
                databaseType: selectedDatabase,
                ...(isExternal && selectedConnection ? { connectionId: selectedConnection } : {}),
                parameters: params
            });
            
            setValidationResult(result);
            const hasErrors = (result.syntaxErrors && result.syntaxErrors.length > 0) || (result.vulnerabilities && result.vulnerabilities.length > 0);
            if ((!result.planLines || result.planLines.length === 0) && !hasErrors) {
                showNotification('Explain plan could not be generated. Ensure the query is valid and specific to Oracle databases.', 'info');
            }
        } catch (error: any) {
            showNotification(`Failed to fetch explain plan: ${error.message}`, 'error');
        } finally {
            setIsExplainingPlan(false);
        }
    };

    const handleAiExplainPlan = async () => {
        if (!validationResult?.planLines || isGeneratingAiExplanation) return;
        
        setIsGeneratingAiExplanation(true);
        try {
            const explanation = await queryService.explainPlanWithAi(
                query?.sqlContent || '',
                validationResult.planLines,
                aiModelName
            );
            setAiExplanation(explanation);
        } catch (error: any) {
            showNotification(`AI explanation failed: ${error.message}`, 'error');
        } finally {
            setIsGeneratingAiExplanation(false);
        }
    };

    const handleCloseExplainPlan = () => {
        setValidationResult((prev: any) => prev ? { ...prev, planLines: [], syntaxErrors: [], vulnerabilities: [] } : null);
        setAiExplanation(null);
    };

    return {
        state: {
            query,
            isLoading,
            error,
            tabValue,
            setTabValue,
            deleteDialogOpen,
            setDeleteDialogOpen,
            selectedTerminal,
            setSelectedTerminal,
            selectedDatabase,
            setSelectedDatabase,
            selectedConnection,
            setSelectedConnection,
            executionParameters,
            executionError,
            lastExecutionResult,
            versionHistory,
            schedules,
            isExecuting: executeMutation.isPending,
            isDeleting: deleteMutation.isPending,
            isToggling: toggleActivationMutation.isPending,
            isSettingCurrentVersion: setCurrentVersionMutation.isPending,
            
            // Version creation state
            versionDialogOpen,
            loadingVersionNumber,
            dialogError,
            versionForm,
            isCreatingVersion: createVersionMutation.isPending,
            isExplainingPlan,
            aiExplanation,
            isGeneratingAiExplanation,
            validationResult,
            aiModelOptions,
            aiModelName,
            
            // Permissions
            canCreateVersions,
            canActivateQueries
        },
        handlers: {
            handleExecute,
            handleParameterChange,
            handleCopyToClipboard,
            handleDelete: deleteMutation.mutate,
            handleToggleActive: () => {
                if (toggleActivationMutation.isPending) return;
                toggleActivationMutation.mutate(!qRef.current?.isActive);
            },
            handleNavigateBack: () => navigate('/queries'),
            handleNavigateEdit: () => navigate(`/queries/${queryId}/edit`),
            handleNavigateCreateSchedule: () => navigate('/schedules', { state: { prefillQueryId: queryId, prefillVersion: query?.currentVersion } }),
            handleSetCurrentVersion: (versionNumber: string) => {
                if (setCurrentVersionMutation.isPending) return;
                setCurrentVersionMutation.mutate(versionNumber);
            },
            
            // Version creation handlers
            handleCreateVersion,
            handleVersionSubmit,
            handleVersionDialogClose: () => {
                setVersionDialogOpen(false);
                setDialogError(null);
                setVersionForm(prev => ({ ...prev, makeCurrent: true }));
            },
            setVersionForm,
            handleExplainPlan,
            handleAiExplainPlan,
            handleCloseExplainPlan,
            setAiModelName,
        }
    };
};
